import {FC} from 'react'
import {useTranslation} from 'react-i18next'
import {useLocation} from 'react-router-dom'
import classNames from 'classnames'
import {useSelector} from 'react-redux'

import {
  SelectedItemsTypeForActionButton,
  CustomApplicationBypassListItemFromServer,
  CustomAddBypassFromNotificationsPayload,
} from 'interfaces'
import {useActions} from 'hooks'
import {
  bypassURLPrefix,
  colorCodes,
  windowsApplicationExtensions,
} from 'definitions/constant'
import {
  LoadingSpinner,
  RenderIf,
  TooltipBody,
  TooltipWrapper,
} from 'components/Basic'
import {selectUser} from 'features/User'

interface ActionButtonProps {
  selectedItems: SelectedItemsTypeForActionButton
  isByAppViewSelected: boolean
  handler: (
    bypassPayload: CustomAddBypassFromNotificationsPayload,
  ) => Promise<void>
  isLoading: boolean
}

interface CreateBypassPayloadType {
  parentItems: string[]
  childItems: string[]
  isByAppViewSelected: boolean
}

export const ActionButton: FC<ActionButtonProps> = ({
  selectedItems,
  isByAppViewSelected,
  handler,
  isLoading,
}) => {
  const {t} = useTranslation(['common'])
  const {parentItems, childItems} = selectedItems
  const {pathname} = useLocation()
  const {isSnackbarActionAvailable} = useActions()

  const disableActionButton =
    pathname?.includes('/policies/bypass') && isSnackbarActionAvailable
  const {emailId: currentUserEmail} = useSelector(selectUser)

  const createBypassPayload = ({
    parentItems,
    childItems,
    isByAppViewSelected,
  }: CreateBypassPayloadType): CustomAddBypassFromNotificationsPayload => {
    const windowsList: CustomApplicationBypassListItemFromServer[] = []
    const macList: CustomApplicationBypassListItemFromServer[] = []

    if (isByAppViewSelected) {
      const combinedItems = [...parentItems, ...childItems]
      combinedItems.forEach(item => {
        const isNewExeOfWindows: boolean = windowsApplicationExtensions.some(
          (extension: string) => item?.toLowerCase().includes(extension),
        )
        const additionalNewFieldItems = {
          note: 'Added via notifications panel',
          updatedBy: currentUserEmail,
          updatedAt: new Date().toISOString(),
        }
        if (isNewExeOfWindows) {
          windowsList.push({
            name: item,
            ...additionalNewFieldItems,
          })
        } else {
          macList.push({
            name: item,
            ...additionalNewFieldItems,
          })
        }
      })
      return {
        customApplicationBypassList: {
          Windows: windowsList,
          Mac: macList,
        },
      }
    }

    return {
      customDomainBypassList: [
        ...parentItems.map(url => {
          return {
            name: url,
            note: 'Added via notifications panel',
            updatedBy: currentUserEmail,
            updatedOn: new Date().toISOString(),
          }
        }),
        ...childItems.map(url => {
          return {
            name: `${bypassURLPrefix}${url}`,
            note: 'Added via notifications panel',
            updatedBy: currentUserEmail,
            updatedOn: new Date().toISOString(),
          }
        }),
      ],
    }
  }

  return (
    <div className="flex items-center">
      <button
        data-tooltip-id="add-ssl-error-bypass-action-tooltip"
        className={classNames(
          'border ml-6 mb-2 rounded-l-full rounded-r-full w-32 h-6 karla-bold text-10px flex flex-row items-center justify-center group',
          {
            'border-white bg-white border-opacity-50 text-dope-black':
              !disableActionButton,
            'hover:bg-opacity-75': !disableActionButton && !isLoading,
            'border-gray-1200 text-gray-1200': disableActionButton,
          },
        )}
        data-cy="ssl-error-bypass-action-button"
        onClick={e => {
          const createBypassArgs = {
            parentItems,
            childItems,
            isByAppViewSelected,
          }

          const payload = createBypassPayload(createBypassArgs)

          handler(payload)
          e.stopPropagation()
        }}
        disabled={disableActionButton || isLoading}
      >
        <RenderIf isTrue={!isLoading}>
          <span>{t('BYPASS_FOR_ALL_POLICIES')}</span>
        </RenderIf>
        <RenderIf isTrue={isLoading}>
          <LoadingSpinner
            type="TailSpin"
            color={colorCodes.dopeBlack}
            width={14}
            height={14}
          />
        </RenderIf>
      </button>
      <TooltipWrapper
        id="add-ssl-error-bypass-action-tooltip"
        noArrow
        place="top-start"
        hidden={!disableActionButton}
        opacity={1}
        disableStyleInjection
        noPadding
      >
        <TooltipBody classes="px-3 py-2">
          {t('SAVE_POLICY_BYPASS_CHANGES')}
        </TooltipBody>
      </TooltipWrapper>
    </div>
  )
}
